import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect
} from "react";
import { Button, Card, CardBody, Row, Col } from "reactstrap";
import { FormattedMessage } from 'react-intl';
import moment from "moment";
import DatePicker from 'react-datepicker';
import ro from 'date-fns/locale/ro';
import enUs from 'date-fns/locale/en-US';
import { useSelector } from 'react-redux';
import { ROMANIAN_LOCALE } from '../../../../languages';
import { UncontrolledTooltip } from 'reactstrap';
import { useIntl } from 'react-intl';

const CalendarButton = React.forwardRef( (props, ref) => {
  const {
    applyDateFilters,
    setDateFilter,
    filterKey,
    name,
    type,
    tooltip,
    entity,
    displayDateFilters,
    shortName,
    datePickerStartDate,
    datePickerEndDate,
  } = props
  const [ showCalendar, setShowCalendar ] = useState( false );
  const [ startDate, setStartDate ] = useState( new Date() );
  const [ endDate, setEndDate ] = useState( new Date() );
  const [ dateRange, setDateRange ] = useState( {
    start: new Date(),
    end: new Date()
  } )

  React.useImperativeHandle( ref, () => ( {
    onReset,
  } ) );

  const lang = useSelector( store => store?.locale?.lang )
  const intl = useIntl();
  const containerRef = useRef( null )

  const onDateChange = useCallback( (dates) => {
    const [ start, end ] = dates;
    setStartDate( start );
    setEndDate( end );
  }, [] );

  const onCancel = useCallback( () => {
    setShowCalendar( false )
    setStartDate( dateRange.start )
    setEndDate( dateRange.end )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [] );

  const onSubmit = useCallback( () => {
    if (startDate?.toDateString() === endDate?.toDateString()) {
      setStartDate( dateRange.start );
      setEndDate( dateRange.end );
      setShowCalendar( false );
    }

    if (startDate) {
      const selectedEndDate = endDate || startDate; // Use startDate if endDate is null

      updateDateFilter( startDate, selectedEndDate );

      setShowCalendar( false );
      setDateRange( { start: startDate, end: selectedEndDate } );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ startDate, endDate ] );

  const onReset = useCallback( () => {
    setStartDate( new Date() );
    setEndDate( null );
    setDateRange( { start: new Date(), end: null } );
  }, [] );


  const updateDateFilter = useCallback( (startDate, selectedEndDate) => {
    if (moment( startDate ).unix() === moment( selectedEndDate ).unix()) {
      applyDateFilters(
        {
          values: [ moment( startDate ).format( 'YYYY/MM/DD' ) ],
          key: filterKey,
          name: shortName[lang],
          type: type,
          entity: entity,
        },
      );
    } else {
      applyDateFilters(
        {
          values: [
            [
              moment( startDate ).format( 'YYYY/MM/DD' ),
              moment( selectedEndDate ).format( 'YYYY/MM/DD' )
            ].join( ' - ' )
          ],
          key: filterKey,
          name: name[lang],
          type: type,
          entity: entity,
        },
      );
    }

    setDateFilter( startDate, selectedEndDate );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ lang ] );

  const isSelectedCalendarFilter = useMemo( () => {
    if (! Object.keys( displayDateFilters ).length) {
      return false;
    }

    let existFilter = false;

    Object.keys( displayDateFilters ).forEach( key => {
      if (displayDateFilters[key].entity === entity) {
        existFilter = true;
      }
    } );

    return existFilter;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ displayDateFilters ] );

  useEffect( () => {
    function handleClickOutside(event) {
      if (containerRef.current && ! containerRef.current.contains( event.target )) {
        const isDatePickerClick = event.target.closest( '.react-datepicker' ) ||
          event.target.closest( '.react-datepicker' ) ||
          event.target.closest( '.react-datepicker__month-container' ) ||
          event.target.closest( '.react-datepicker__header ' ) ||
          event.target.closest( '.react-datepicker__month-dropdown ' ) ||
          event.target.closest( '.react-datepicker__year-dropdown ' ) ||
          event.target.closest( '.react-datepicker__day ' ) ||
          event.target.closest( '.react-datepicker__navigation ' ) ;

        if (! isDatePickerClick) {
          setShowCalendar( false );
        }

      }
    }

    document.addEventListener( 'click', handleClickOutside );
    return () => {
      document.removeEventListener( 'click', handleClickOutside );
    };
  }, [] );

  return (
    <>
      <div ref={containerRef}>
        <Button
          className={`calendar-btn m-4 ${isSelectedCalendarFilter ? 'selected-date-picker' : ''}`}
          id={`calendar-button-${tooltip}`}
          onClick={() => {
            setShowCalendar( ! showCalendar )
            setStartDate( dateRange.start )
            setEndDate( dateRange.end )
          }}>
          <i className="fa fa-calendar-alt"></i>
        </Button>
        <UncontrolledTooltip
          delay={0}
          target={`calendar-button-${tooltip}`}
          modifiers={
            {
              preventOverflow: {
                enabled: true,
                boundariesElement: 'body'
              }
            }
          }
        >
          <FormattedMessage id={`button.filters.${tooltip}`}/>
        </UncontrolledTooltip>
        {showCalendar &&
          <div className="date-picker-container">
            <Card className="custom-date-picker-card">
              <DatePicker
                key={`datePicker_${startDate.toString()}`}
                startDate={startDate}
                endDate={endDate}
                onChange={onDateChange}
                yearDropdownItemNumber={100}
                locale={lang === ROMANIAN_LOCALE.lang ? ro : enUs}
                maxDate={datePickerEndDate ?  datePickerEndDate : new Date()}
                minDate={new Date(new Date().setFullYear(datePickerStartDate,0,1)) }
                selectsRange
                showMonthDropdown
                showYearDropdown
                withPortal
                inline
                scrollableYearDropdown
              />
              <Card className="secondary-date-picker-content">
                <CardBody>
                  <input className="input-date-range"
                         placeholder={intl.formatMessage( { id: 'button.filters.placeholder.start_date' } )}
                         value={startDate ? moment( startDate ).format( "DD/MM/YYYY" ) : ""}
                         readOnly/>
                  <input className="input-date-range"
                         placeholder={intl.formatMessage( { id: 'button.filters.placeholder.end_date' } )}
                         value={endDate ? moment( endDate ).format( "DD/MM/YYYY" ) : ""}
                         readOnly/>
                  <Row>
                    <Col>
                      <Button
                        className="btn btn-default btn-secondary-date-range p-2 mt-2 w-100"
                        onClick={onCancel}>
                        <FormattedMessage id="button.cancel"/>
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        className="btn btn-default btn-secondary-date-range p-2 mt-2 w-100"
                        onClick={onSubmit}>
                        <FormattedMessage id="button.add"/>
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        className="btn btn-default btn-secondary-date-range p-2 mt-2 w-100"
                        onClick={onReset}>
                        <FormattedMessage id="button.reset"/>
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Card>
          </div>
        }
      </div>
    </>
  )
} );

export { CalendarButton }
